import { useEffect } from 'react';
import { CMTextSizeExperiment } from '../../shared/types/contentMarketing';

export const useTextBalancer = (
    ref,
    doScaleText: CMTextSizeExperiment,
    disable?: boolean,
) => {
    useEffect(() => {
        if (disable || doScaleText === 'challengerNoTextManipulation') return;
        if (ref.current) {
            try {
                const init = textBalancer(
                    ref.current,
                    doScaleText === 'challengerFullWidthText',
                );
                init();
            } catch (e) {
                console.error(e);
            }
        }
    }, [doScaleText, ref, disable]);
};

/**
 * I reworked this lib: https://github.com/nytimes/text-balancer
 * because it did not seem to be maintained anymore
 * - Jørgen Lybeck Hansen
 * @param element
 * @param doScaleText
 */
function textBalancer(element: HTMLElement, doScaleText: boolean): () => void {
    const candidates: HTMLElement[] = [element];

    // HELPER FUNCTION -- initializes recursive binary search
    // Make the headline element as narrow as possible while maintaining its current height (number of lines). Binary search.
    function squeezeContainer(headline, originalHeight, bottomRange, topRange) {
        let mid;
        if (bottomRange >= topRange) {
            headline.style.maxWidth = topRange + 'px';
            return;
        }
        mid = (bottomRange + topRange) / 2;
        headline.style.maxWidth = mid + 'px';

        if (headline.clientHeight > originalHeight) {
            // we've squeeze too far and headline has spilled onto an additional line; recurse on wider range
            squeezeContainer(headline, originalHeight, mid + 1, topRange);
        } else {
            // headline has not wrapped to another line; keep squeezing!
            squeezeContainer(headline, originalHeight, bottomRange + 1, mid);
        }
    }

    // function to see if a headline is multiple lines
    // we only want to break if the headline is multiple lines
    //
    // We achieve this by turning the first word into a span
    // and then we compare the height of that span to the height
    // of the entire headline. If the headline is bigger than the
    // span by 10px we balance the headline.
    function textElementIsMultipleLines(element) {
        let firstWordHeight,
            elementHeight,
            HEIGHT_OFFSET,
            elementWords,
            firstWord,
            ORIGINAL_ELEMENT_TEXT;

        ORIGINAL_ELEMENT_TEXT = element.innerHTML;

        // usually there is around a 5px discrepency between
        // the first word and the height of the whole headline
        // so subtract the height of the headline by 10 px and
        // we should be good
        HEIGHT_OFFSET = 10;

        // get all the words in the headline as
        // an array -- will include punctuation
        //
        // this is used to put the headline back together
        elementWords = element.innerHTML.split(' ');

        // make span for first word and give it an id
        // so we can access it in le dom
        firstWord = document.createElement('span');
        firstWord.id = 'element-first-word';
        firstWord.innerHTML = elementWords[0];

        // this is the entire headline
        // as an array except for first word
        //
        // we will append it to the headline after the span
        elementWords = elementWords.slice(1);

        // empty the headline and append the span to it
        element.innerHTML = '';
        element.appendChild(firstWord);

        // add the rest of the element back to it
        element.innerHTML += ' ' + elementWords.join(' ');

        // update the first word variable in the dom
        firstWord = document.getElementById('element-first-word');

        firstWordHeight = firstWord?.offsetHeight || 0;
        elementHeight = element?.offsetHeight || 0;
        // restore the original element text
        element.innerHTML = ORIGINAL_ELEMENT_TEXT;

        // compare the height of the element and the height of the first word
        return elementHeight - HEIGHT_OFFSET > firstWordHeight;
    } // end textElementIsMultipleLines

    return () => {
        let element;
        let i;

        for (i = 0; i < candidates.length; i += 1) {
            element = candidates[i];

            if (textElementIsMultipleLines(element)) {
                element.style.maxWidth = '';
                squeezeContainer(
                    element,
                    element.clientHeight,
                    0,
                    element.clientWidth,
                );

                if (doScaleText) {
                    const ratio =
                        element.parentElement.clientWidth / element.clientWidth;

                    const percentage = ratio * 100;
                    console.log({ ratio, percentage });
                    element.style.fontSize = `${percentage}%`;
                    element.style.lineHeight = `110%`;
                    element.style.maxWidth = '100%';
                }
            }
        }
    };
} // end textBalancer
